// PTZ 云台控制按钮
// internal API: base-service /ptz/ptz_start + /ptz/ptz_stop GET
// 反编译参数: device_auto_id (数字 device_id) + channel_id + type + speed
// type: 1上 2下 3左 4右 5ZoomIn 6ZoomOut, speed: 1-5
//
// RTL 注意：阿拉伯语下"左/右"按钮在视觉上需要相反
// （UI 上"左按钮"位置反到右边，但语义仍然是"摄像头向左转"）
import { Button, Space, Tooltip, message } from 'antd';
import {
	UpOutlined, DownOutlined, LeftOutlined, RightOutlined,
	ZoomInOutlined, ZoomOutOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isRTL, type Locale } from '@ulucu/shared';
import { callInternal } from '../api/internal.js';

interface PtzControlsProps {
	/** 数字 device_id（不是 SN） */
	deviceAutoId: string | number;
	channelId?: string | number;
	speed?: 1 | 2 | 3 | 4 | 5;
	disabled?: boolean;
}

type PtzType = 1 | 2 | 3 | 4 | 5 | 6;

export default function PtzControls({ deviceAutoId, channelId = 1, speed = 3, disabled }: PtzControlsProps) {
	const { i18n, t } = useTranslation();
	const rtl = isRTL(i18n.language as Locale);
	const [activeType, setActiveType] = useState<PtzType | null>(null);
	const LeftIcon = rtl ? RightOutlined : LeftOutlined;
	const RightIcon = rtl ? LeftOutlined : RightOutlined;
	const [permissionWarned, setPermissionWarned] = useState(false);

	const callPtz = async (action: 'start' | 'stop', type: PtzType) => {
		try {
			const res = await callInternal({
				short_name: 'base',
				action: action === 'start' ? '/ptz/ptz_start' : '/ptz/ptz_stop',
				method: 'GET',
				query: { device_auto_id: String(deviceAutoId), channel_id: String(channelId), type, speed },
				av: '1',
			});
			// 303010 = ulucu 后端账号功能权限点 100010041001 未开通
			if (action === 'start' && Number(res.code) === 303010 && !permissionWarned) {
				message.warning(t('ptz.no_permission'), 4);
				setPermissionWarned(true);
			}
		} catch (e) {
			console.warn('PTZ failed:', (e as Error).message);
		}
	};

	const onPress = (type: PtzType) => {
		setActiveType(type);
		void callPtz('start', type);
	};
	const onRelease = (type: PtzType) => {
		setActiveType(null);
		void callPtz('stop', type);
	};

	const btn = (type: PtzType, icon: React.ReactNode, tip: string) => (
		<Tooltip title={tip}>
			<Button
				icon={icon}
				disabled={disabled}
				type={activeType === type ? 'primary' : 'default'}
				onMouseDown={() => onPress(type)}
				onMouseUp={() => onRelease(type)}
				onMouseLeave={() => activeType === type && onRelease(type)}
				onTouchStart={() => onPress(type)}
				onTouchEnd={() => onRelease(type)}
				size="large"
			/>
		</Tooltip>
	);

	return (
		<Space direction="vertical" align="center" size="small">
			<Space>
				<div style={{ width: 40 }} />
				{btn(1, <UpOutlined />, '上')}
				<div style={{ width: 40 }} />
			</Space>
			<Space>
				{btn(3, <LeftIcon />, '左')}
				<div style={{ width: 40 }} />
				{btn(4, <RightIcon />, '右')}
			</Space>
			<Space>
				<div style={{ width: 40 }} />
				{btn(2, <DownOutlined />, '下')}
				<div style={{ width: 40 }} />
			</Space>
			<Space style={{ marginTop: 8 }}>
				{btn(5, <ZoomInOutlined />, '放大')}
				{btn(6, <ZoomOutOutlined />, '缩小')}
			</Space>
		</Space>
	);
}
